class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = Array.from(
      document.querySelectorAll('#cart-icon-bubble, [data-pianzi-cart-button]')
    );

    cartLinks.forEach((cartLink) => {
      if (cartLink.dataset.pianziDrawerBound === 'true') return;

      cartLink.dataset.pianziDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    this.dispatchEvent(
      new CustomEvent('pianzi:cart-drawer-closed', {
        bubbles: true
      })
    );
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) return;

    /*
      Read the empty/populated state from Shopify's newly rendered cart-drawer
      section before replacing the drawer contents. The <cart-drawer> element
      itself is not replaced by #CartDrawer.innerHTML, so its is-empty class
      must be synchronized manually after every AJAX quantity/removal update.
    */
    const cartDrawerSectionHtml = parsedState.sections['cart-drawer'];
    const renderedDocument = cartDrawerSectionHtml
      ? new DOMParser().parseFromString(cartDrawerSectionHtml, 'text/html')
      : null;
    const renderedDrawer = renderedDocument?.querySelector('cart-drawer');
    const renderedItems = renderedDocument?.querySelector('cart-drawer-items');
    const isEmpty = renderedDrawer
      ? renderedDrawer.classList.contains('is-empty')
      : renderedItems?.classList.contains('is-empty') || Number(parsedState.item_count) === 0;

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (replacement === null) return;
      sectionElement.innerHTML = replacement;
    });

    /* Synchronize empty-state classes after the refreshed markup is injected. */
    this.classList.toggle('is-empty', Boolean(isEmpty));
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', Boolean(isEmpty));
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', Boolean(isEmpty));

    /* Always return the refreshed drawer to its intended starting position. */
    this.pianziResetDrawerScroll();
    const emptyPanel = this.querySelector('.drawer__inner-empty');
    if (emptyPanel) emptyPanel.scrollTop = 0;

    /* Re-bind elements that were replaced by the section render. */
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    setTimeout(() => this.open(), 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const target = documentFragment.querySelector(selector);
    return target ? target.innerHTML : null;
  }

  pianziResetDrawerScroll() {
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.scrollTop = 0;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  /*
    PIANZI Phase 11 Part 3B
    Use the cart line key for drawer mutations and replace the complete drawer
    from Shopify's returned sections. This avoids Dawn's line-index/focus
    reconciliation error after one of several items is removed.
  */
  async updateQuantity(line, quantity, event, name, variantId) {
    const quantityInput =
      this.querySelector(`#Drawer-quantity-${line}`) || this.querySelector(`#Quantity-${line}`);
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;

    this.enableLoading?.(line);

    const payload = {
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname
    };

    if (lineKey) payload.id = lineKey;
    else payload.line = Number(line);

    try {
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify(payload)
      });
      const state = await response.json();

      if (!response.ok || state.errors) {
        throw new Error(state.description || state.message || state.errors || window.cartStrings.error);
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer?.renderContents && state.sections) {
        drawer.renderContents(state);
      } else {
        await this.onCartUpdate();
      }

      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = '';

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: state,
        variantId: lineVariantId
      });
    } catch (error) {
      console.error('PIANZI cart drawer update failed:', error);
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message || window.cartStrings.error;
      this.dispatchCartErrorEvent?.(error.message || window.cartStrings.error, 'SERVICE_UNAVAILABLE');
    } finally {
      if (this.isConnected) this.disableLoading?.(line);
    }
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}


/* PIANZI CART DRAWER — PHASE 11 PART 2 */
(() => {
  if (window.__pianziCartLookBound) return;
  window.__pianziCartLookBound = true;

  document.addEventListener('change', (event) => {
    const selector = event.target.closest('[data-pianzi-look-variant]');
    if (!selector || selector.tagName !== 'SELECT') return;
    const card = selector.closest('[data-pianzi-cart-recommendation]');
    const selected = selector.options[selector.selectedIndex];
    const price = card?.querySelector('[data-pianzi-look-price]');
    const addButton = card?.querySelector('[data-pianzi-look-add]');
    if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
    if (addButton) addButton.disabled = selected?.disabled || !selector.value;
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pianzi-look-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.classList.contains('is-loading')) return;

    const card = button.closest('[data-pianzi-cart-recommendation]');
    const variantControl = card?.querySelector('[data-pianzi-look-variant]');
    const variantId = Number(variantControl?.value);
    const label = button.querySelector('[data-pianzi-look-add-label]');
    const status = card?.querySelector('[data-pianzi-look-status]');
    const defaultLabel = button.dataset.defaultLabel || 'ADD TO BAG';
    if (!variantId) return;

    button.classList.add('is-loading');
    button.disabled = true;
    if (label) label.textContent = 'ADDING…';
    if (status) status.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      });
      const state = await response.json();
      if (!response.ok) throw new Error(state.description || state.message || 'Unable to add this item.');

      const drawer = document.querySelector('cart-drawer');
      if (state.sections && drawer?.renderContents) {
        drawer.renderContents(state);
      } else {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('The item was added, but the bag could not refresh.');
        window.location.reload();
      }
    } catch (error) {
      button.classList.remove('is-loading');
      button.disabled = false;
      if (label) label.textContent = defaultLabel;
      if (status) status.textContent = error.message || 'Unable to add this item.';
    }
  });
})();

/* PIANZI CART DRAWER — PHASE 11 PART 3A: CUSTOM VARIANT DROPDOWN */
(() => {
  if (window.__pianziCartLookDropdownBound) return;
  window.__pianziCartLookDropdownBound = true;

  const closeDropdown = (dropdown, restoreFocus = false) => {
    if (!dropdown) return;
    const trigger = dropdown.querySelector('[data-pianzi-look-dropdown-trigger]');
    const menu = dropdown.querySelector('.pianzi-cart-look__dropdown-menu');
    dropdown.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (menu) menu.hidden = true;
    if (restoreFocus) trigger?.focus();
  };

  const closeAll = (except = null) => {
    document.querySelectorAll('[data-pianzi-look-dropdown].is-open').forEach((dropdown) => {
      if (dropdown !== except) closeDropdown(dropdown);
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pianzi-look-dropdown-trigger]');
    if (trigger) {
      event.preventDefault();
      const dropdown = trigger.closest('[data-pianzi-look-dropdown]');
      const menu = dropdown?.querySelector('.pianzi-cart-look__dropdown-menu');
      const shouldOpen = !dropdown?.classList.contains('is-open');
      closeAll(dropdown);
      if (shouldOpen && dropdown && menu) {
        dropdown.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
      } else {
        closeDropdown(dropdown);
      }
      return;
    }

    const option = event.target.closest('[data-pianzi-look-dropdown-option]');
    if (option && !option.disabled) {
      event.preventDefault();
      const dropdown = option.closest('[data-pianzi-look-dropdown]');
      const card = option.closest('[data-pianzi-cart-recommendation]');
      const input = dropdown?.querySelector('[data-pianzi-look-variant]');
      const label = dropdown?.querySelector('[data-pianzi-look-dropdown-label]');
      const price = card?.querySelector('[data-pianzi-look-price]');
      const addButton = card?.querySelector('[data-pianzi-look-add]');
      if (input) input.value = option.dataset.value || '';
      if (label) label.textContent = option.dataset.label || option.textContent.trim();
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      dropdown?.querySelectorAll('[data-pianzi-look-dropdown-option]').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
      if (addButton) addButton.disabled = !input?.value;
      closeDropdown(dropdown, true);
      input?.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (!event.target.closest('[data-pianzi-look-dropdown]')) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const dropdown = event.target.closest('[data-pianzi-look-dropdown]') || document.querySelector('[data-pianzi-look-dropdown].is-open');
    if (dropdown) {
      event.preventDefault();
      closeDropdown(dropdown, true);
    }
  });
})();
